<template>
  <div
    class="upload-container"
    :class="{ 'drag-over': is_drag_over }"
    :style="`min-height: ${data.min_height || '80px'}`"
    @dragover.prevent="handleDragOver"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
    @click="$refs.inputFile.click()"
  >
    <div class="upload-content h-100" v-if="!file">
      <div class="d-flex align-items-center">
        <component :is="data.icon.type" :icon="data.icon.name" class="mr-05" />
        <strong>{{ data.title }}</strong>
      </div>
      <p class="m-0">{{ data.subtitle }}</p>
    </div>

    <div v-else class="w-100 position-relative h-100">
      <img
        :src="preview"
        class="preview-full w-100 h-100"
      />

      <div class="overlay-buttons d-flex align-items-center justify-content-end p-1">
        <div class="flex-column">
          <slot name="metrics"></slot>
          <b-button
            id="msj-deleted"
            class="button-animation"
            variant="button-animation"
            @click.prevent.stop="removeFile"
          >
          <div><feather-icon icon="TrashIcon" class="icon" size="21"></feather-icon></div>
          </b-button>
          <b-popover
            target="msj-deleted"
            triggers="hover"
            placement="top"
          >
            <template #default>
              <h4>{{ $t('campaigns.msjDelete') }}</h4>
            </template>
          </b-popover>
        </div>
      </div>
    </div>

    <input
      type="file"
      :accept="data.extensions_allowed"
      ref="inputFile"
      @change="handleFileUpload"
      class="input-file-hidden"
    />
  </div>
</template>

<script>
import { BButton, BAvatar, BIcon, BPopover } from 'bootstrap-vue';
import { getThumbnails } from 'video-metadata-thumbnails';
import { showToast } from '@/libs/utils/others';

export default {
  name: 'inputDragAndDropFile',
  props: {
    data: {
      type: Object,
      required: false
    },
    max_size_mb: {
      type: Number,
      default: 10
    }
  },
  components: {
    BButton,
    BAvatar,
    BIcon,
    BPopover,
  },
  data() {
    return {
      blob_preview: null,
      getThumbnails,
      file: null,
      is_drag_over: false,
      preview: null,
    };
  },
  methods: {
    validateFile(file) {
      // Validate format extensions
      if (this.data.extensions_allowed) {
        const allowed_extensions = this.data.extensions_allowed.split(',').map(ext => ext.trim().replace('.', '').toLowerCase());
        
        const file_extension = file.name.split('.').pop().toLowerCase();
        const file_type = file.type.split('/')[1]?.toLowerCase();
        
        const is_valid_extension = allowed_extensions.includes(file_extension) || allowed_extensions.includes(file_type);
        
        if (!is_valid_extension) {
          showToast(this, 'danger', 'XIcon', 'errorTitle', 'errors.extensionNotSupported');
          return false;
        }
      }
      
      // Validate size of file
      const file_size_mb = file.size / (1024 * 1024);
      if (file_size_mb > this.max_size_mb) {
        showToast(this, 'danger', 'XIcon', 'errorTitle', 'creator.fileBigTitle');
        return false;
      }
      
      return true;
    },
    removeFile() {
      this.file = null
      this.$emit('file_loaded', null)
    },
    hasChanged() {
      setTimeout(() => {
        this.blob_preview = null;
        const url_blob = URL.createObjectURL(this.file);

        if (this.file.type.startsWith('video/')) {
          getThumbnails(url_blob, { quality: 0.6 })
            .then((value) => {
              this.preview = URL.createObjectURL(value[0].blob);
              this.blob_preview = value[0].blob;
              this.$emit('blob_preview', this.blob_preview);
            })
        } else if (this.file.type.startsWith('image/')) {
          this.preview = url_blob;
          this.$emit('blob_preview', null);
        } else {
          this.preview = null;
          this.$emit('blob_preview', null);
        }
      }, 200);
    },

    handleFileUpload(event) {
      const selected_file = event.target.files[0];
      
      if (!selected_file) return;
      
      // Validate file
      if (!this.validateFile(selected_file)) {
        this.$refs.inputFile.value = null;
        return;
      }
      
      this.file = selected_file;
      this.hasChanged();
      this.$emit('file_loaded', this.file);
      this.$refs.inputFile.value = null;
    },
    getExtension() {
      try {
        const extension = this.file.type.split('/')[1];
        return this.data.extensions_allowed.includes(extension) ? extension : 'file';
      } catch (error) {
        return 'file';
      }
    },
    handleDragOver() {
      this.is_drag_over = true;
    },
    handleDragLeave() {
      this.is_drag_over = false;
    },
    handleDrop(event) {
      const files = event.dataTransfer.files;
      this.is_drag_over = false;
      
      if (files.length === 0) return;
      
      const dropped_files = files[0];
      
      // Validate file
      if (!this.validateFile(dropped_files)) {
        return;
      }
      
      this.file = dropped_files;
      this.hasChanged();
      this.$emit('file_loaded', this.file);
    }
  }
};
</script>

<style lang="scss" scoped>
.preview-full {
  object-fit: contain;
  border-radius: 0.4rem;
  background-color: #e5e5e6;
  color: #444;
}
.button-animation {

    background-color: white;
    border: none;
    border-radius: 100%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover {
      background-color: #e6e6e6;
    }
    .icon {
      color: #000 !important;
      font-size: 24px;
    }
  }

.w-100.position-relative {
  border-radius: 0.4rem;
  overflow: hidden;
}
.overlay-buttons {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}
.btn-metrics:focus {
  background-color: transparent
}
.upload-container {
  border: 1px dashed #3483fa;
  border-radius: 0.4rem;
  padding: 0;
  cursor: pointer;
  transition: border 0.2s, outline 0.2s;
  height: 100%;
  .w-80 {
    width: 80%;
  }

  &:hover {
    outline: 2px solid #3483fa;
  }

  &.drag-over {
    background-color: #e0f0ff;
    border: 2px dashed #3483fa;
  }
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  strong {
    display: block;
  }

  p {
    font-size: 13px;
    color: #6d6b77;
  }
}

.input-file-hidden {
  display: none;
}
</style>
